import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from '@app/store';
import {
  createProcessRole,
  updateProcessRole,
  fetchProcessRoleById,
  processRoleActions,
} from "./processRoleSlice";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  SwapVerticalCircle as SwapVerticalCircleIcon,
} from "@mui/icons-material";
import {
  ProcessRoleFieldType,
  ProcessRoleFieldTypeLabels,
  ProcessRoleFieldAdditionalData,
  ProcessRoleOutputDTO,
} from './types';
import { ApiError } from '@/types/api';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { processRoleMapper } from "./processRoleMapper";
import { useNotification } from '@hooks/useNotification';

import styled from "styled-components";
import { DeleteButton } from "@components/ui/DeleteButton";

const DropZone = styled.div`
  height: ${(props) => (props.hidden ? "0px" : "20px")};
  ${(props) => (props.hidden ? "padding: 0px;" : "")};
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%; /* Ocupa toda a largura disponível */
`;

const LinhaSuperior = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const BoxContainer = styled.div`
  flex-shrink: 0; /* Não deixa o Box crescer */
  margin-right: 8px; /* Espaçamento entre os itens */
`;

const FormContainer = styled.div`
  flex-grow: 1; /* Ocupa todo o espaço restante */
`;

const DropZoneContainer = styled.div`
  width: 100%; /* Ocupa toda a largura */
`;

const ProcessRoleForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.processRole
  );
  const [formData, setFormData] = useState<Partial<ProcessRoleOutputDTO>>({});
  const [localFields, setLocalFields] = useState<ProcessRoleFieldAdditionalData[]>([]);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  const [dragged, setDragged] = useState<number | null>(null); // storing the dragged item as an index
  const [mouse, setMouse] = useState<[number, number]>([0, 0]);
  const [dropZone, setDropZone] = useState(0);

  // drop item
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dragged !== null) {
        e.preventDefault();
        setDragged(null);

        if (
          dragged < 0 ||
          dragged >= localFields.length ||
          dropZone < 0 ||
          dropZone >= localFields.length
        ) {
          console.error("Índices inválidos.");
          return;
        }

        const novolocalFields = [...localFields]; // Cria uma cópia para evitar mutação
        const [itemRemovido] = novolocalFields.splice(dragged, 1); // Remove o item da posição B
        novolocalFields.splice(
          dropZone + (dragged <= dropZone ? 0 : 1),
          0,
          itemRemovido
        ); // Insere o item na posição C

        setLocalFields(novolocalFields);
      }
    };

    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  });

  // get closest drop zone
  useEffect(() => {
    if (dragged !== null) {
      // get all drop-zones
      const elements = Array.from(document.getElementsByClassName("drop-zone"));
      // get all drop-zones' y-axis position
      const positions = elements.map((e) => [
        e.id,
        e.getBoundingClientRect().top,
      ]);
      // get the difference with the mouse's y position
      const absDifferences = positions.map((v: any) =>
        Math.abs(v[1] - mouse[1])
      );

      // get the item closest to the mouse
      let result = absDifferences.indexOf(Math.min(...absDifferences));
      result += dragged <= result ? 1 : 0;
      console.log(mouse, dragged, result, positions);
      setDropZone(result);
    }
  }, [dragged, mouse]);

  // get mouse coordinates
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      setMouse([e.x, e.y]);
    };

    document.addEventListener("mousemove", handler);

    return () => document.removeEventListener("mousemove", handler);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do ProcessRole:", id);
        isLoadingRef.current = true;

        if (id) {
          console.log("📡 PhaseFormForm - Carregando ProcessRole:", id);
          const resultAction = await dispatch(fetchProcessRoleById(id));

          if (fetchProcessRoleById.fulfilled.match(resultAction)) {
            console.log("✅ ProcessRole carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar ProcessRole:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(processRoleActions.clearCurrentItem());
    };
  }, [dispatch, id, notification]);

  useEffect(() => {
    if (currentItem) {
      console.log(
        "📝 ProcessRole - Atualizando dados do formulário:",
        currentItem
      );

      setFormData({
        name: currentItem.name,
        description: currentItem.description,
      });

      if (currentItem.additionalData) {
        setLocalFields(currentItem.additionalData);
      }
    }
  }, [currentItem]);

  const fields: Field[] = [
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "description",
      label: "Descrição",
      type: "textarea",
      rows: 4,
      validationError: formError?.validationErrors?.description?.message,
    },
  ];

  const handleFieldUpdate = (
    index: number,
    updatedField: ProcessRoleFieldAdditionalData
  ) => {
    console.log("Updating field at index:", index, updatedField); // Debug
    const newFields = [...localFields];
    newFields[index] = updatedField;
    setLocalFields(newFields);
  };

  const handleSubmit = async (data: Partial<ProcessRoleOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      const finalData = {
        ...data,
        additionalData: localFields,
      };

      const mappedData = processRoleMapper.toDTO(finalData);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updateProcessRole({ id, data: mappedData }))
        : await dispatch(createProcessRole(mappedData));

      if (
        updateProcessRole.rejected.match(resultAction) ||
        createProcessRole.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/process-roles");
    } catch (error) {
      console.error("❌ ProcessRoleForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  const handleAddField = () => {
    setLocalFields((prev) => [
      ...prev,
      {
        name: "",
        type: ProcessRoleFieldType.STRING,
      },
    ]);
  };

  const handleFieldRemove = (index: number) => {
    setLocalFields((prev) => prev.filter((_, i) => i !== index));
  };

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (isInitialLoad && loading) {
    return <FormSkeleton fields={2} additionalFields={true} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {id ? "Editar Personagem" : "Novo Personagem"}
      </Typography>
      <DataForm
        data={formData}
        fields={fields}
        onSubmit={handleSubmit}
        basePath="asset-types"
        title={id ? "Editar Personagem" : "Novo Personagem"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />

      <Box sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Campos Adicionais</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              handleAddField();
              setExpandedIndex(localFields.length); // Expand the newly added field
              setTimeout(() => {
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: "smooth"
                });
              }, 150);
            }}
            variant="outlined"
            size="small"
          >
            Adicionar Campo
          </Button>
        </Box>

        {localFields.map((field, index) => (
          <Box key={index}>
            {dragged === index  && (
              <div
                style={{
                  left: `${mouse[0]}px`,
                  top: `${mouse[1]}px`,
                  position: "fixed",
                  boxShadow: "5px 5px 10px rgba(0,0,0,0.5)",
                }}
              >
                <Box>{localFields[dragged].name}</Box>
              </div>
            )}
            {dragged !== index && (
              <Container>
                <LinhaSuperior>
                  <FormContainer>
                    <DataItemForm
                      field={field}
                      index={index}
                      id={"field-" + index}
                      onUpdate={handleFieldUpdate}
                      onRemove={handleFieldRemove}
                      error={formError}
                      setDragged={setDragged}
                      expanded={expandedIndex === index}
                      setExpandedIndex={setExpandedIndex}
                    />
                  </FormContainer>
                </LinhaSuperior>
                <DropZoneContainer className="drop-zone">
                  <DropZone
                    hidden={dragged === null || dropZone !== index}
                    color="red"
                  ></DropZone>
                </DropZoneContainer>
              </Container>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface DataItemFormProps {
  field: ProcessRoleFieldAdditionalData;
  index: number;
  id: string;
  onUpdate: (index: number, data: ProcessRoleFieldAdditionalData) => void;
  onRemove: (index: number) => void;
  error?: any;
  setDragged?: any;
  expanded: boolean;
  setExpandedIndex: (index: number | null) => void;
}

const DataItemForm: React.FC<DataItemFormProps> = ({
  field,
  index,
  id,
  onUpdate,
  onRemove,
  error,
  setDragged,
  expanded,
  setExpandedIndex,
}) => {
  const handleExpandChange = () => {
    setExpandedIndex(expanded ? null : index); // Collapse if already expanded, otherwise expand
  };

  const handleFieldChange = (name: string, value: any) => {
    onUpdate(index, {
      ...field,
      [name]: value,
    });
  };

  const handleTypeChange = (newType: ProcessRoleFieldType) => {
    console.log("handleTypeChange:", newType);
    onUpdate(index, {
      ...field,
      type: newType,
      options: undefined,
    });
  };

  const baseFields: Field[] = [
    {
      name: "name",
      label: "Nome do Campo",
      type: "text",
      required: true,
    },
    {
      name: "type",
      label: "Tipo do Campo",
      type: "select",
      required: true,
      options: Object.entries(ProcessRoleFieldTypeLabels).map(
        ([value, label]) => ({
          value,
          label,
        })
      ),
      value: field.type,
      onChange: (value) => handleTypeChange(value as ProcessRoleFieldType),
    },
    {
      name: "required",
      label: "Obrigatório?",
      type: "checkbox",
      required: false,
      value: field.required,
    },
  ];

  return (
    <Accordion id={id} expanded={expanded} onChange={handleExpandChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BoxContainer>
          <Box
            key={"key" + index}
            onMouseDown={(e) => {
              e.preventDefault();
              setDragged(index);
            }}
          >
            <SwapVerticalCircleIcon />
          </Box>
        </BoxContainer>

        <Typography>{field.name || "Campo sem nome"}</Typography>

        <DeleteButton
          sx={{ marginRight: 2 }}
          messageText="Confirma a remoção do item?"
          onRemove={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
        />
      </AccordionSummary>
      <AccordionDetails>
        <DataForm
          data={field}
          fields={baseFields}
          onSubmit={async () => Promise.resolve()}
          basePath=""
          title=""
          hideButtons
          onChange={(name, value) => handleFieldChange(name, value)}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessRoleForm;